"use client"
import styled from "styled-components"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { FiGithub, FiExternalLink } from "react-icons/fi"

// --- STYLED COMPONENTS ---

const ProjectsSection = styled.section`
  padding: 6rem 0;
  background: transparent; // To see the global gradient
  position: relative;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #2A9D8F,rgb(6, 124, 110));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: #2A9D8F;
  }
`

const ImageContainer = styled.a`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-size: cover;
  background-position: center;
  background-image: ${({ $image }) => $image ? `url(${$image})` : 'linear-gradient(135deg, #264653, #2A9D8F)'};
`

const CardContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1B1B1E;
  margin-bottom: 0.75rem;
`

const ProjectDescription = styled.p`
  font-size: 0.95rem;
  color: #1B1B1E;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  span {
    padding: 0.25rem 0.75rem;
    background: rgba(38, 70, 83, 0.1);
    color: #264653;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 9999px;
  }
`

const CardFooter = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
`

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &.primary {
    background-color: #264653;
    color: #F4F1DE;
    &:hover {
      background-color: #2A9D8F;
    }
  }

  &.secondary {
    background-color: transparent;
    color: #264653;
    border: 1px solid #264653;
    &:hover {
      background-color: rgba(38, 70, 83, 0.1);
    }
  }
`

// --- DATA (Populated from your new projects and GitHub) ---

const projectsData = [
  {
    title: "Kimelia Luxe",
    description: "Kimelia Luxe is a modern fashion platform built with React.js, featuring a virtual fitting room, AI-based style suggestions, and e-commerce tools for selling, stock, and revenue management.",
    technologies: ["React", "JavaScript", "Styled-Components"],
    githubUrl: "https://github.com/Aline-CROIRE/KIMELIA-LUX.git",
    liveUrl: "https://kimelia-lux.vercel.app/",
    image: "/kimelia.png",
  },
  {
    title: "SelfGrow - Journaling App",
    description: "A comprehensive personal growth mobile app built with React Native and Expo, featuring authentication, data persistence, journaling, and goal tracking.",
    technologies: ["React Native", "Expo", "Postgres SQL","TypeScript", "Mobile Dev"],
    githubUrl: "https://github.com/Aline-CROIRE",
    liveUrl: null, // No live URL for mobile apps typically
    image: "/journal.png", // Use a placeholder or a screenshot
  },
  {
    title: "SectorFlow",
    description: "A dynamic business sector selection interface for a SaaS platform. Customizes the user experience based on the chosen industry, demonstrating conditional UI logic.",
    technologies: ["React", "Vite","JavaScript", "Responsive Design"],
    githubUrl: "https://github.com/Aline-CROIRE/SectorFlow.git",
    liveUrl: "https://sector-flow.vercel.app/select-sector",
    image: "/sector.png",
  },
  {
    title: "Procurement System API",
    description: "A robust backend API for a procurement management system, documented with Swagger. Handles authentication, user roles, and core procurement logic.",
    technologies: ["Node.js", "Express", "Swagger", "PostgreSQL", "REST API"],
    githubUrl: "https://github.com/Aline-CROIRE/Procurement-Backend.git",
    liveUrl: "https://procurement-backend-1.onrender.com/api-docs/",
    image: "/Procure.png",
  },
  {
    title: "Botiga E-commerce",
    description: "A feature-rich e-commerce front-end with product categories, search functionality, and a clean user interface for a seamless shopping experience.",
    technologies: ["HTML", "CSS", "JavaScript", "React", "E-commerce"],
    githubUrl: "https://github.com/Aline-CROIRE/BotigaShop.git",
    liveUrl: "https://botigashop2.netlify.app/",
    image: "/botiga.png",
  },
  
];


// --- MAIN COMPONENT ---

const Projects = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <ProjectsSection id="projects" ref={ref}>
      <Container>
        <SectionHeader variants={itemVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <h2>
            Featured <span className="highlight">Projects</span>
          </h2>
          <p>A selection of my work. Feel free to explore the code and live demos.</p>
        </SectionHeader>

        <ProjectsGrid variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
          {projectsData.map((project) => (
            <ProjectCard key={project.title} variants={itemVariants}>
              <ImageContainer 
                href={project.liveUrl || project.githubUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                $image={project.image}
              />
              <CardContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <TechStack>
                  {project.technologies.map((tech) => (
                    <span key={tech}>{tech}</span>
                  ))}
                </TechStack>
                <CardFooter>
                  <ActionLink href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="secondary">
                    <FiGithub size={16} />
                    View Code
                  </ActionLink>
                  {project.liveUrl && (
                    <ActionLink href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="primary">
                      <FiExternalLink size={16} />
                      Live Demo
                    </ActionLink>
                  )}
                </CardFooter>
              </CardContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>
    </ProjectsSection>
  );
};

export default Projects;